"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  CircleActionHoverFx,
  CircleActionParticleBurst,
} from "./composites/circle-action-hover-fx";
import {
  circleActionVariantCssVars,
  resolveCircleActionVariant,
  sidebarCircleActionVariantCssVars,
  type CircleActionVariant
} from "./circle-action-variants";

const circleActionButtonVariants = cva(
  [
    "circle-action-btn cab-frost-idle group relative inline-flex items-center rounded-full border-0",
    "text-slate-500 dark:text-zinc-500",
    "motion-safe:[transition:background_360ms_ease,box-shadow_480ms_cubic-bezier(0.16,1,0.3,1),transform_320ms_cubic-bezier(0.16,1,0.3,1),backdrop-filter_360ms_ease]",
    "active:scale-[0.97]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-2",
    "dark:focus-visible:ring-sky-500/40 dark:focus-visible:ring-offset-zinc-950",
    "select-none",
  ].join(" "),
  {
    variants: {
      size: {
        md: "h-12",
        sm: "h-9",
        xs: "h-8",
      },
      active: {
        true: "cab-frost-idle-off is-active text-white dark:text-white",
        false: "",
      },
      expandable: {
        true: [
          "cab-expandable cab-fx-host justify-start overflow-hidden",
          "hover:overflow-visible",
          "hover:w-max hover:max-w-[min(22rem,calc(100vw-3rem))]",
          "motion-safe:hover:scale-[1.02]",
          "motion-safe:[transition:max-width_720ms_cubic-bezier(0.22,1,0.36,1),min-width_720ms_cubic-bezier(0.22,1,0.36,1),padding_680ms_cubic-bezier(0.22,1,0.36,1),background_420ms_ease,box-shadow_520ms_cubic-bezier(0.22,1,0.36,1),transform_420ms_cubic-bezier(0.22,1,0.36,1)]",
        ].join(" "),
        false: "justify-center overflow-hidden",
      },
      context: {
        default: "",
        sidebar: "sidebar-circle-action overflow-visible hover:overflow-visible",
      },
      anchor: {
        center: "",
        left: "origin-left",
        right: "origin-right",
      },
    },
    compoundVariants: [
      {
        size: "md",
        expandable: true,
        className: "w-12 max-w-[3rem] px-3 hover:px-3.5 hover:pr-4",
      },
      {
        size: "md",
        expandable: false,
        className: "w-12 px-3",
      },
      {
        size: "sm",
        expandable: true,
        anchor: "left",
        className: "w-9 max-w-[2.25rem] px-2 hover:px-2.5 hover:pr-3",
      },
      {
        size: "sm",
        expandable: true,
        anchor: "left",
        context: "sidebar",
        className:
          "cab-expand-rail w-9 max-w-[2.25rem] px-2 hover:px-2.5 hover:pr-3 motion-safe:hover:scale-100",
      },
      {
        size: "sm",
        expandable: false,
        className: "w-9 px-2",
      },
      {
        size: "xs",
        expandable: true,
        anchor: "left",
        className: "w-8 max-w-[2rem] px-1.5 hover:px-2 hover:pr-2.5",
      },
      {
        size: "xs",
        active: true,
        expandable: true,
        className: "w-max max-w-[min(16rem,calc(100vw-3rem))] overflow-visible px-2 pr-2.5",
      },
      {
        size: "xs",
        expandable: false,
        className: "w-8 px-1.5",
      },
      {
        expandable: true,
        className:
          "dark:hover:shadow-[0_20px_44px_-14px_rgba(0,0,0,0.6)]",
      },
    ],
    defaultVariants: {
      size: "md",
      active: false,
      expandable: false,
      context: "default",
      anchor: "center",
    },
  }
);

const iconSizeClasses = {
  md: "h-6 w-6 group-hover:scale-105 group-data-[active=true]:scale-[1.02]",
  sm: "h-[18px] w-[18px] group-hover:scale-105 group-data-[active=true]:scale-[1.02]",
  xs: "h-[14px] w-[14px] group-hover:scale-105 group-data-[active=true]:scale-[1.02]",
} as const;

const labelSizeClasses = {
  md: "ml-2.5 pr-0.5 text-sm",
  sm: "ml-2 pr-0.5 text-xs",
  xs: "ml-1.5 pr-0 text-[11px]",
} as const;

function CircleActionButtonGlobalStyles() {
  return (
    <style jsx global>{`
        .circle-action-btn .cab-glass-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
          border-radius: inherit;
          pointer-events: none;
          opacity: 0;
          transition: opacity 360ms ease;
        }
        .circle-action-btn .cab-surface {
          position: relative;
          z-index: 2;
        }
        .circle-action-btn.sidebar-circle-action.cab-frost-idle:not(:hover):not([data-active="true"]) {
          overflow: visible;
          box-shadow:
            0 2px 5px rgba(15, 23, 42, 0.04),
            0 7px 16px -9px rgba(15, 23, 42, 0.07),
            var(--gb-tint-shadow) !important;
        }
        .dark .circle-action-btn.sidebar-circle-action.cab-frost-idle:not(:hover):not([data-active="true"]) {
          box-shadow:
            0 2px 5px rgba(0, 0, 0, 0.12),
            0 8px 18px -9px rgba(0, 0, 0, 0.18),
            var(--gb-tint-shadow) !important;
        }
        .circle-action-btn.cab-frost-idle:not(:hover):not([data-active="true"]) {
          isolation: isolate;
          overflow: hidden;
          background: transparent !important;
          backdrop-filter: none;
          -webkit-backdrop-filter: none;
          box-shadow: none !important;
        }
        .circle-action-btn.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-glass-bg {
          opacity: 1;
          background-color: transparent;
          background-image:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.48) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.32) 38%, rgba(255,255,255,0.06) 58%, transparent 72%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.14) 20%, transparent 34%),
            radial-gradient(circle at 80% 84%, var(--cab-glass-moon, rgba(15,23,42,0.1)) 0%, var(--cab-glass-moon, rgba(15,23,42,0.04)) 26%, transparent 42%),
            radial-gradient(circle at 50% 50%, var(--cab-glass-tint, rgba(255,255,255,0.04)) 0%, var(--cab-glass-tint, rgba(255,255,255,0.04)) 100%);
          backdrop-filter: blur(22px) saturate(1.35);
          -webkit-backdrop-filter: blur(22px) saturate(1.35);
          box-shadow:
            inset -1px -1px 0 var(--cab-glass-edge, rgba(255,255,255,0.72)),
            inset -1px 0 0 rgba(255,255,255,0.2),
            inset 0 -1px 0 rgba(255,255,255,0.34),
            inset 0 0 0 1px rgba(255,255,255,0.3),
            inset 1px 1px 0 rgba(15,23,42,0.06),
            var(--cab-glass-glow, 0 8px 24px -12px rgba(15,23,42,0.12));
        }
        .dark .circle-action-btn.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-glass-bg {
          background-image:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.22) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.1) 40%, transparent 70%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 20%, transparent 34%),
            radial-gradient(circle at 80% 84%, var(--cab-glass-moon-dark, rgba(0,0,0,0.22)) 0%, var(--cab-glass-moon-dark, rgba(0,0,0,0.08)) 26%, transparent 42%),
            radial-gradient(circle at 50% 50%, var(--cab-glass-tint-dark, rgba(255,255,255,0.03)) 0%, var(--cab-glass-tint-dark, rgba(255,255,255,0.03)) 100%);
          backdrop-filter: blur(22px) saturate(1.25) brightness(0.96);
          -webkit-backdrop-filter: blur(22px) saturate(1.25) brightness(0.96);
          box-shadow:
            inset -1px -1px 0 var(--cab-glass-edge-dark, rgba(255,255,255,0.24)),
            inset -1px 0 0 rgba(255,255,255,0.1),
            inset 0 -1px 0 rgba(255,255,255,0.16),
            inset 0 0 0 1px rgba(255,255,255,0.14),
            inset 1px 1px 0 rgba(0,0,0,0.22),
            var(--cab-glass-glow-dark, 0 10px 28px -14px rgba(0,0,0,0.4));
        }
        .circle-action-btn.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-icon img {
          filter: var(--cab-icon-filter-idle, brightness(0) saturate(100%) invert(68%));
          opacity: 0.92;
          transition: filter 300ms ease, opacity 300ms ease;
        }
        .dark .circle-action-btn.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-icon img {
          filter: var(--cab-icon-filter-idle-dark, brightness(0) saturate(100%) invert(0.48));
          opacity: 0.72;
        }
        .circle-action-btn.cab-frost-idle:not(.sidebar-circle-action):not(:hover):not([data-active="true"]) .cab-icon svg {
          position: relative;
          z-index: 1;
          color: rgb(51 65 85);
          opacity: 1;
          filter: none;
          transition: color 300ms ease, opacity 300ms ease;
        }
        .dark .circle-action-btn.cab-frost-idle:not(.sidebar-circle-action):not(:hover):not([data-active="true"]) .cab-icon svg {
          color: rgb(212 212 216);
          opacity: 1;
        }
        /* Sidebar idle — mismo vidrio e iconos que GlassBadgeGroup */
        .circle-action-btn.sidebar-circle-action.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-glass-bg {
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.42) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.62) 0%, rgba(255,255,255,0.22) 38%, rgba(255,255,255,0.04) 58%, transparent 72%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 20%, transparent 34%),
            var(--gb-tint-bg);
          backdrop-filter: blur(22px) saturate(1.2);
          -webkit-backdrop-filter: blur(22px) saturate(1.2);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.58),
            inset -1px  0   0 rgba(255,255,255,0.16),
            inset  0   -1px 0 rgba(255,255,255,0.24),
            inset  0    0   0 1px var(--gb-tint-border),
            inset  1px  1px 0 rgba(15,23,42,0.04);
        }
        .dark .circle-action-btn.sidebar-circle-action.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-glass-bg {
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.14) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.06) 40%, transparent 70%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 20%, transparent 34%),
            var(--gb-tint-bg);
          backdrop-filter: blur(22px) saturate(1.15) brightness(0.98);
          -webkit-backdrop-filter: blur(22px) saturate(1.15) brightness(0.98);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.16),
            inset -1px  0   0 rgba(255,255,255,0.08),
            inset  0   -1px 0 rgba(255,255,255,0.10),
            inset  0    0   0 1px var(--gb-tint-border),
            inset  1px  1px 0 rgba(0,0,0,0.16);
        }
        .dark .circle-action-btn.sidebar-circle-action.cab-frost-idle:not([data-active="true"]):hover .cab-glass-bg {
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.18) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0.07) 40%, transparent 70%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.11) 0%, rgba(255,255,255,0.03) 20%, transparent 34%),
            color-mix(in srgb, var(--gb-tint-bg) 50%, rgba(32,32,40,0.72));
          backdrop-filter: blur(22px) saturate(1.08) brightness(0.80);
          -webkit-backdrop-filter: blur(22px) saturate(1.08) brightness(0.80);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.20),
            inset -1px  0   0 rgba(255,255,255,0.09),
            inset  0   -1px 0 rgba(255,255,255,0.12),
            inset  0    0   0 1px color-mix(in srgb, var(--gb-tint-border) 70%, rgba(15,23,42,0.4)),
            inset  1px  1px 0 rgba(0,0,0,0.18);
        }
        .circle-action-btn.sidebar-circle-action.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-icon img,
        .circle-action-btn.sidebar-circle-action.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-icon svg {
          filter: var(--cab-icon-filter-idle, none) saturate(0.72);
          opacity: 0.68;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .circle-action-btn.sidebar-circle-action.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-icon img,
        .dark .circle-action-btn.sidebar-circle-action.cab-frost-idle:not(:hover):not([data-active="true"]) .cab-icon svg {
          filter: var(--cab-icon-filter-idle-dark, none) saturate(0.72);
          opacity: 0.54;
        }
        .circle-action-btn.sidebar-circle-action::before {
          content: "";
          position: absolute;
          left: 50%;
          bottom: -0.5rem;
          z-index: 0;
          width: calc(100% + 0.75rem);
          min-width: 2.75rem;
          height: 1.35rem;
          border-radius: 9999px;
          pointer-events: none;
          opacity: 0;
          transform: translateX(-50%) scale(0.72);
          filter: blur(12px);
          background:
            radial-gradient(ellipse at center,
              rgba(var(--cab-shadow-rgb, 79, 70, 229), 0.46) 0%,
              rgba(var(--cab-shadow-rgb, 79, 70, 229), 0.28) 36%,
              rgba(var(--cab-shadow-rgb, 79, 70, 229), 0.08) 62%,
              transparent 78%);
          transition:
            opacity 300ms ease,
            transform 360ms cubic-bezier(0.16,1,0.3,1),
            width 520ms cubic-bezier(0.22,1,0.36,1);
        }
        .dark .circle-action-btn.sidebar-circle-action::before {
          background:
            radial-gradient(ellipse at center,
              rgba(var(--cab-shadow-rgb, 79, 70, 229), 0.36) 0%,
              rgba(var(--cab-shadow-rgb, 79, 70, 229), 0.18) 36%,
              rgba(var(--cab-shadow-rgb, 79, 70, 229), 0.06) 62%,
              transparent 78%);
        }
        .circle-action-btn.sidebar-circle-action:hover::before {
          opacity: 0.82;
          transform: translateX(-50%) scale(1);
        }
        .dark .circle-action-btn.sidebar-circle-action:not([data-active="true"]):hover::before {
          opacity: 0.48;
        }
        .circle-action-btn.sidebar-circle-action[data-active="true"]::before {
          opacity: 0.46;
          transform: translateX(-50%) scale(0.92);
        }
        .circle-action-btn.sidebar-circle-action:hover,
        .circle-action-btn.sidebar-circle-action[data-active="true"] {
          filter: saturate(0.86);
        }
        .circle-action-btn:hover {
          background: var(--cab-hover-bg) !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          box-shadow: var(--cab-hover-shadow) !important;
          color: #fff !important;
        }
        .circle-action-btn.is-active,
        .circle-action-btn[data-active="true"] {
          background: var(--cab-active-bg) !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          box-shadow: var(--cab-active-shadow) !important;
          color: #fff !important;
        }
        .circle-action-btn:hover .cab-icon img,
        .circle-action-btn[data-active="true"] .cab-icon img {
          filter: brightness(0) invert(1);
          opacity: 1;
        }
        .circle-action-btn:hover.cab-fx-host::after,
        .circle-action-btn[data-active="true"].cab-fx-host::after {
          opacity: 0 !important;
        }
        .circle-action-btn:hover .cab-fx-layer {
          opacity: 0.72;
          z-index: 1;
        }
        .circle-action-btn[data-active="true"] .cab-fx-layer {
          opacity: 0.38;
          z-index: 1;
        }
        .circle-action-btn.cab-expandable:not(:hover):not([data-active="true"]) {
          overflow: hidden;
        }
        .circle-action-btn.cab-expandable::after {
          content: none;
        }
        .cab-fx-host .cab-surface {
          position: relative;
          z-index: 2;
        }
        .circle-action-btn[data-size="xs"] .cab-icon img,
        .circle-action-btn[data-] .cab-icon img {
          transform: scale(0.92);
          transform-origin: center;
        }
        .circle-action-btn[data-size="sm"]:hover .cab-icon img,
        .circle-action-btn[data-][data-active="true"] .cab-icon img,
        .circle-action-btn[data-size="xs"]:hover .cab-icon img,
        .circle-action-btn[data-][data-active="true"] .cab-icon img {
          transform: scale(1);
        }
        .circle-action-btn:hover .cab-fx-particles,
        .cab-fx-host:hover .cab-fx-particles {
          opacity: 1;
          transition: none;
        }
        .circle-action-btn:hover .cab-fx-particle,
        .cab-fx-host:hover .cab-fx-particle {
          animation: cab-particle-burst 0.45s linear 1 forwards;
          animation-delay: var(--cab-p-delay, 0ms);
        }
      `}</style>
  );
}

export interface CircleActionButtonProps
  extends Omit<VariantProps<typeof circleActionButtonVariants>, "variant"> {
  children: React.ReactNode;
  label?: React.ReactNode;
  /** Paleta visual del botón (14 variantes). */
  variant?: CircleActionVariant;
  /** @deprecated Usar `variant`. */
  tone?: CircleActionVariant;
  as?: "button" | "div";
  type?: "button" | "submit" | "reset";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  /** When true + active=true, label stays permanently visible (not only on hover). */
  pinLabel?: boolean;
  context?: "default" | "sidebar";
  anchor?: "center" | "left" | "right";
  "aria-label"?: string;
}

export function CircleActionButton({
  children,
  label,
  variant: variantProp,
  tone,
  active,
  expandable,
  context,
  anchor,
  size: sizeProp,
  as = "button",
  type = "button",
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled,
  pinLabel = false,
  "aria-label": ariaLabel,
}: CircleActionButtonProps) {
  const size = sizeProp ?? (context === "sidebar" ? "sm" : "md");
  const resolvedVariant = resolveCircleActionVariant(
    variantProp ?? tone ?? undefined
  );
  const variantStyle =
    context === "sidebar"
      ? sidebarCircleActionVariantCssVars(resolvedVariant)
      : circleActionVariantCssVars(resolvedVariant);

  const classes = circleActionButtonVariants({
    active,
    expandable,
    context,
    anchor,
    size,
  });

  const showFx = Boolean(expandable) && size !== "xs";
  const isAnchorRight = anchor === "right";

  const sharedProps = {
    className: classes,
    style: variantStyle,
    "data-active": active ? "true" : "false",
    "data-variant": resolvedVariant,
    "data-tone": resolvedVariant,
    "data-anchor": anchor ?? "center",
    "data-size": size,
  } as const;

  const body = (
    <>
      <span className="cab-glass-bg" aria-hidden />
      {showFx && (
        <CircleActionHoverFx variant={resolvedVariant} size={size} />
      )}
      <span
        className={cn(
          "cab-surface relative z-[2] flex min-w-0 items-center",
          isAnchorRight && "flex-row-reverse"
        )}
      >
        <span
          className={cn(
            "cab-icon relative z-10 flex shrink-0 items-center justify-center overflow-visible",
            "motion-safe:transition-transform motion-safe:duration-350 motion-safe:ease-out",
            iconSizeClasses[size]
          )}
        >
          {showFx && (
            <CircleActionParticleBurst
              variant={resolvedVariant}
              size={size}
            />
          )}
          <span className="relative z-[1] flex items-center justify-center">
            {children}
          </span>
        </span>
        {label ? (
          <span
            className={cn(
              "cab-label-text relative z-10 shrink-0 whitespace-nowrap font-medium tracking-[-0.01em]",
              isAnchorRight ? "mr-2 ml-0 pr-0.5 pl-0 text-xs" : labelSizeClasses[size],
              isAnchorRight
                ? "translate-x-[8px] opacity-0 blur-[2px]"
                : "translate-x-[-8px] opacity-0 blur-[2px]",
              "max-w-0 overflow-hidden pointer-events-none invisible",
              "motion-safe:transition-[opacity_420ms_cubic-bezier(0.22,1,0.36,1)_80ms,max-width_680ms_cubic-bezier(0.22,1,0.36,1),transform_680ms_cubic-bezier(0.22,1,0.36,1),filter_420ms_ease,margin_680ms_cubic-bezier(0.22,1,0.36,1),padding_680ms_cubic-bezier(0.22,1,0.36,1),visibility_280ms_ease]",
              "group-hover:visible group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100 group-hover:blur-0 group-hover:max-w-[14rem]",
              active && pinLabel
                ? "visible pointer-events-auto translate-x-0 opacity-100 blur-0 max-w-[14rem] text-white dark:text-white"
                : active
                  ? "text-white dark:text-white"
                  : "text-inherit group-hover:text-white dark:group-hover:text-white"
            )}
          >
            {label}
          </span>
        ) : null}
      </span>
    </>
  );

  if (as === "div") {
    return (
      <>
        <div {...sharedProps}>{body}</div>
        <CircleActionButtonGlobalStyles />
      </>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled}
      {...sharedProps}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel}
    >
      {body}
      <CircleActionButtonGlobalStyles />
    </button>
  );
}

export type { CircleActionVariant } from "./circle-action-variants";
export {
  CIRCLE_ACTION_VARIANTS,
  resolveCircleActionVariant
} from "./circle-action-variants";
